import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import V3Header from '../components/V3Header';
import V3Footer from '../components/V3Footer';
import BlogMeta from '../components/BlogMeta';
import ShareButtons from '../components/ShareButtons';
import ReadingProgress from '../components/ReadingProgress';
import { useLanguage } from '../LanguageContext';
import { blogPosts, getBlogPost } from '../content/blogPosts';
import { BLOG_VISUALS } from '../components/visuals/BlogVisuals';
import { breadcrumbJsonLd } from '../seoText';
import useSeo from '../useSeo';

import '../V3.css';

const SITE = 'https://brahmoid.org';

const V3BlogPost = () => {
  const { t } = useLanguage();
  const { slug } = useParams();
  const post = getBlogPost(slug);

  useSeo(
    post
      ? {
          title: `${post.title} | BRAHMOID`,
          description: post.excerpt,
          path: `/blog/${post.slug}`,
          type: 'article',
          image: post.image,
          imageWidth: post.imageWidth,
          imageHeight: post.imageHeight,
          imageAlt: post.imageAlt,
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.excerpt,
              inLanguage: 'hi',
              datePublished: post.date,
              dateModified: post.dateModified || post.date,
              articleSection: post.category,
              image: post.image ? `${SITE}${post.image}` : undefined,
              author: post.authorLinkedin
                ? { '@type': 'Person', name: post.author, url: post.authorLinkedin }
                : { '@type': 'Organization', name: post.author },
              publisher: {
                '@type': 'Organization',
                name: 'BRAHMOID',
                url: SITE,
                logo: { '@type': 'ImageObject', url: `${SITE}/favicon.png` },
              },
              mainEntityOfPage: `${SITE}/blog/${post.slug}`,
            },
            breadcrumbJsonLd([
              { name: 'BRAHMOID', url: `${SITE}/` },
              { name: 'Blog', url: `${SITE}/blog` },
              { name: post.title, url: `${SITE}/blog/${post.slug}` },
            ]),
          ],
        }
      : {}
  );

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const Visual = BLOG_VISUALS[post.slug];
  const currentIndex = blogPosts.findIndex((p) => p.slug === post.slug);
  const nextPost = blogPosts.length > 1 ? blogPosts[(currentIndex + 1) % blogPosts.length] : null;

  return (
    <div className="v3-app">
      <div className="v3-page">
        <ReadingProgress />
        <V3Header activeNav="blog" />
        <article className="v3-blog-article">
          <Link to="/blog" className="v3-blog-back">{t('common.blogBack')}</Link>

          {post.category && <span className="v3-blog-tag">{post.category}</span>}
          <h1>{post.title}</h1>

          <BlogMeta
            author={post.author}
            authorLinkedin={post.authorLinkedin}
            dateLabel={post.dateLabel}
            readTime={post.readTime}
            byLabel={t('common.blogBy')}
          />

          <ShareButtons url={`${SITE}/blog/${post.slug}`} title={post.title} />

          {post.image && (
            <figure className="v3-blog-hero-figure">
              <img src={post.image} alt={post.imageAlt || post.title} loading="eager" />
              {post.imageAlt && <figcaption className="v3-blog-hero-caption">{post.imageAlt}</figcaption>}
            </figure>
          )}
          {!post.image && Visual && <Visual title={post.title} />}

          {post.sections.map((section, i) => (
            <React.Fragment key={section.heading || i}>
              <section>
                {section.heading && <h2>{section.heading}</h2>}
                {section.paragraphs && section.paragraphs.map((p) => <p key={p}>{p}</p>)}
                {section.image && (
                  <figure className="v3-blog-hero-figure v3-blog-inline-figure">
                    <img src={section.image} alt={section.imageAlt || ''} loading="lazy" />
                    {section.imageAlt && <figcaption className="v3-blog-hero-caption">{section.imageAlt}</figcaption>}
                  </figure>
                )}
                {section.list && (
                  <ul>
                    {section.list.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                )}
                {section.afterList && section.afterList.map((p) => <p key={p}>{p}</p>)}
              </section>
              {i === 0 && post.pullQuote && (
                <blockquote className="v3-blog-pullquote">{post.pullQuote}</blockquote>
              )}
            </React.Fragment>
          ))}

          {nextPost && (
            <div className="v3-blog-next">
              <div className="v3-blog-next-label">अगला लेख</div>
              <Link to={`/blog/${nextPost.slug}`} className="v3-blog-next-card">
                <span className="v3-blog-next-title">{nextPost.title}</span>
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          )}
        </article>
        <V3Footer />
      </div>
    </div>
  );
};

export default V3BlogPost;
